/**
 * 侧边栏导航 — 从 route-policies catalog 投影，不再平行维护 path/permission。
 *
 * Callers: DashboardSidebar、漂移测试
 *
 * Modules:
 * - workspaceNavItems / workspaceSecondaryNavItems / adminNavItems
 * - filterNavItemsByPermission / resolveWorkspaceMenuItems
 */
import type {
  WorkspacePermission,
  WorkspaceStatus,
} from "@metaadsmanager/tk-master-contracts";
import { Persons, Sliders } from "@gravity-ui/icons";
import type { AppIcon } from "@/components/ui/app-icon";

import {
  normalizeApplicationPathname,
  toStaticExportHref,
} from "./normalize-application-pathname";
import {
  adminRoutePolicies,
  resolveApplicationRouteFromPolicies,
  workspaceRoutePolicies,
  type ApplicationMode,
  type ApplicationRoute,
  type RouteNavGroup,
  type RoutePolicy,
} from "./route-policies";

export type { ApplicationMode, ApplicationRoute };
export { normalizeApplicationPathname, toStaticExportHref };

/** 单个导航项；支持嵌套子菜单 */
export type NavItem = {
  labelKey: string;
  href?: string;
  icon: AppIcon;
  /** 侧栏门闸：需具备该权限才可见（与 catalog.requiredPermission 对齐） */
  permission?: WorkspacePermission;
  /** 仅当前工作空间的所有者可见，不对应可分配权限 key。 */
  ownerOnly?: boolean;
  children?: NavItem[];
};

function policiesInGroup(group: RouteNavGroup): RoutePolicy[] {
  return workspaceRoutePolicies
    .filter((policy) => policy.nav?.group === group)
    .slice()
    .sort((left, right) => (left.nav?.order ?? 0) - (right.nav?.order ?? 0));
}

function leafFromPolicy(policy: RoutePolicy): NavItem {
  return {
    labelKey: policy.nav!.labelKey,
    href: policy.pathname,
    icon: policy.nav!.icon,
    permission: policy.access.requiredPermission,
    ownerOnly: policy.access.requiresWorkspaceOwner === true,
  };
}

const primaryLeaves = policiesInGroup("primary").map(leafFromPolicy);
const toolLeaves = policiesInGroup("tools").map(leafFromPolicy);
const teamLeaves = policiesInGroup("team").map(leafFromPolicy);
const secondaryLeaves = policiesInGroup("secondary").map(leafFromPolicy);

/** 工作区主操作导航（Business Center / Audience 暂隐藏，目录中无 nav） */
export const workspaceNavItems: NavItem[] = [
  ...primaryLeaves,
  {
    labelKey: "shell.workspaceNavigation.tools",
    icon: Sliders,
    children: toolLeaves,
  },
];

/** 工作区次级导航（团队管理 / 授权 / 设置） */
export const workspaceSecondaryNavItems: NavItem[] = [
  {
    labelKey: "shell.workspaceNavigation.teamManagement",
    icon: Persons,
    children: teamLeaves,
  },
  ...secondaryLeaves,
];

/** 平台管理导航 */
export const adminNavItems: NavItem[] = adminRoutePolicies
  .filter((policy) => policy.nav?.group === "admin")
  .slice()
  .sort((left, right) => (left.nav?.order ?? 0) - (right.nav?.order ?? 0))
  .map(leafFromPolicy);

const pausedVisiblePathnames = new Set(
  workspaceRoutePolicies
    .filter((policy) => policy.visibleWhenPaused)
    .map((policy) => policy.pathname),
);

function isVisibleWhenPaused(href: string | undefined): boolean {
  return Boolean(href && pausedVisiblePathnames.has(href));
}

/** 解析应用壳当前页面标题及其所属侧栏入口（委托 route-policies）。 */
export function resolveApplicationRoute(
  mode: ApplicationMode,
  pathname: string,
): ApplicationRoute | undefined {
  return resolveApplicationRouteFromPolicies(mode, pathname);
}

/** 按路径在导航树中查找匹配项（含子级）。 */
export function findNavItemByPathname(
  items: readonly NavItem[],
  pathname: string,
): NavItem | undefined {
  const normalized = normalizeApplicationPathname(pathname);
  for (const item of items) {
    if (item.href && normalizeApplicationPathname(item.href) === normalized) {
      return item;
    }
    if (item.children) {
      const child = findNavItemByPathname(item.children, normalized);
      if (child) return child;
    }
  }
  return undefined;
}

/** 过滤需权限或所有者身份的导航项（含子级；无可见子项且无 href 的父级一并隐藏）。 */
export function filterNavItemsByPermission(
  items: readonly NavItem[],
  permissions: readonly WorkspacePermission[],
  isWorkspaceOwner = false,
  checkPermissions = true,
): NavItem[] {
  return items.flatMap((item) => {
    if (item.ownerOnly && !isWorkspaceOwner) return [];
    if (item.children) {
      const children = filterNavItemsByPermission(
        item.children,
        permissions,
        isWorkspaceOwner,
        checkPermissions,
      );
      if (children.length === 0 && !item.href) return [];
      return [{ ...item, children }];
    }
    if (
      checkPermissions &&
      item.permission &&
      !permissions.includes(item.permission)
    ) {
      return [];
    }
    return [item];
  });
}

function filterNavItemsVisibleWhenPaused(items: readonly NavItem[]): NavItem[] {
  return items.flatMap((item) => {
    if (item.children) {
      const children = filterNavItemsVisibleWhenPaused(item.children);
      if (children.length === 0 && !item.href) return [];
      return [{ ...item, children }];
    }
    if (!item.href || isVisibleWhenPaused(item.href)) return [item];
    return [];
  });
}

/**
 * 刷新期间：未就绪用 stale；就绪后必须以 live 为准（允许变少）。
 */
export function resolveEffectiveNavPermissions(
  membershipReady: boolean,
  permissions: readonly WorkspacePermission[] | undefined,
  stalePermissions: readonly WorkspacePermission[] | undefined,
): readonly WorkspacePermission[] {
  if (!membershipReady) return stalePermissions ?? [];
  return permissions ?? [];
}

export type ResolveWorkspaceMenuItemsInput = {
  /** `/v1/me` 已结算（成功或失败）；未就绪时不得把未知权限当成空集。 */
  membershipReady: boolean;
  permissions?: readonly WorkspacePermission[];
  /** me 未就绪时使用上次成功的权限快照，避免刷新时 Members/Audit 闪灭。 */
  stalePermissions?: readonly WorkspacePermission[];
  workspaceStatus?: WorkspaceStatus;
  /** me 未就绪时使用上次工作区状态，避免暂停主导航先撑开再收缩。 */
  staleWorkspaceStatus?: WorkspaceStatus;
  /** 当前成员身份是否为该工作空间所有者。 */
  isWorkspaceOwner?: boolean;
  /** me 未就绪时使用上次所有者标记，避免角色入口闪现。 */
  staleIsWorkspaceOwner?: boolean;
};

/**
 * 解析工作区侧栏菜单：权限未就绪时乐观展示全部次级项，结算后再按 live 权限过滤。
 */
export function resolveWorkspaceMenuItems({
  membershipReady,
  permissions = [],
  stalePermissions,
  workspaceStatus,
  staleWorkspaceStatus,
  isWorkspaceOwner = false,
  staleIsWorkspaceOwner = false,
}: ResolveWorkspaceMenuItemsInput): NavItem[] {
  const permissionsKnown =
    membershipReady || (stalePermissions?.length ?? 0) > 0;
  const effectivePermissions = resolveEffectiveNavPermissions(
    membershipReady,
    permissions,
    stalePermissions,
  );
  const ownerVisible = membershipReady
    ? isWorkspaceOwner
    : staleIsWorkspaceOwner;

  const status = membershipReady ? workspaceStatus : staleWorkspaceStatus;
  const isPaused = status === "paused";
  const visiblePrimary = isPaused
    ? filterNavItemsVisibleWhenPaused(workspaceNavItems)
    : workspaceNavItems;
  const primaryItems = filterNavItemsByPermission(
    visiblePrimary,
    effectivePermissions,
    ownerVisible,
    permissionsKnown,
  );

  const gatedSecondaryItems = filterNavItemsByPermission(
    workspaceSecondaryNavItems,
    effectivePermissions,
    ownerVisible,
    permissionsKnown,
  );

  const secondaryItems = isPaused
    ? filterNavItemsVisibleWhenPaused(gatedSecondaryItems)
    : gatedSecondaryItems;

  return [...primaryItems, ...secondaryItems];
}
